<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { computed } from 'vue';

import RadioGroup from '@/components/base/form/RadioGroup.vue';
import PostalAddress from '@/components/base/PostalAddress.vue';
import TextButton from '@/components/base/TextButton.vue';
import Caption from '@/components/base/typography/Caption.vue';
import { NutsAddress } from '@/utils/address';

interface Props {
  addresses: NutsAddress[];
  isPending?: boolean;
  modelValue?: string;
  showEditButton?: boolean;
  showSetAsDefaultButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), { showEditButton: true });

const emit = defineEmits<{
  'edit-address': [id: string | undefined];
  'set-as-default': [id: string | undefined];
  'update:modelValue': [id: string];
}>();

const selectedAddressId = useVModel(props, 'modelValue', emit);

const hasDefaultAddress = computed(() => props.addresses.some((address) => address.default));
</script>

<template>
  <div
    aria-label="Shipping Address Radio Options"
    class="overflow-y-auto max-h-96 styled-scrollbar"
    :class="{ 'pr-4': addresses.length > 3 }"
    data-test="addresses-list"
    role="radiogroup"
  >
    <RadioGroup
      v-model="selectedAddressId"
      data-test="address-radio-input"
      :items="addresses"
      :keyGetter="(address) => address.id"
      name="address-list"
      :valueGetter="(address) => address.id"
    >
      <template #default="{ item }">
        <PostalAddress
          :address="item"
          class="font-normal"
          :highlight="selectedAddressId === item.id"
        />
      </template>
      <template #bottom="{ item }">
        <TextButton
          v-if="showEditButton"
          class="text-sm ml-7"
          data-test="address-edit-button"
          :disabled="isPending"
          @click="$emit('edit-address', item.id)"
        >
          Edit address
        </TextButton>
        <TextButton
          v-if="showSetAsDefaultButton && !hasDefaultAddress"
          class="text-sm ml-3"
          data-test="address-set-as-default-button"
          :disabled="isPending"
          @click="$emit('set-as-default', item.id)"
        >
          Set as default
        </TextButton>
      </template>
      <template #right="{ item }">
        <span v-if="item.default" class="py-0.5 px-1.5 h-5 rounded-md bg-neutral-200">
          <Caption class="text-neutral-600">Default</Caption>
        </span>
      </template>
    </RadioGroup>
  </div>
</template>

<style lang="scss" scoped>
.styled-scrollbar {
  &::-webkit-scrollbar {
    width: 6px;
    height: auto;
    background-color: $alto;
    border-radius: 9999px;
    scrollbar-color: $dark-grey $alto;

    &-thumb {
      background: $dark-grey;
      border-radius: 9999px;
    }
  }
}
</style>
